import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";
import { useAutoConnectProjectServers } from "@/hooks/useAutoConnectProjectServers";
import { useProjectServers } from "@/hooks/useViews";
import { useDeselectedConnectors } from "@/lib/playground-connector-selection";
import { sphinxApi } from "@/lib/sphinx-api";
import { invalidateSphinxQueries } from "@/lib/use-sphinx-query";
import { useSharedAppState } from "@/state/app-state-context";
import { useServerActions } from "@/state/server-actions-context";
import type { HostConfigDtoV2 } from "@/lib/client-config-v2";

/** Set-equality for two name lists, order-independent. */
function sameNameSet(a: ReadonlyArray<string>, b: ReadonlyArray<string>) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  for (const name of b) {
    if (!set.has(name)) return false;
  }
  return true;
}

function isActiveRuntimeStatus(status: string | undefined) {
  return status === "connected" || status === "connecting";
}

interface ActiveHostServerReconcilerProps {
  projectId: string | null;
  isAuthenticated: boolean;
  activeHost?: HostConfigDtoV2;
  activeHostId: string | null;
}

/**
 * Mounted once at the App root so host-switch reconciliation (disconnect
 * excess + connect required) fires regardless of which tab the user is on.
 *
 * Previously only `PlaygroundTab` and `ServersTab` mounted
 * `useAutoConnectProjectServers`, so switching hosts from the Tools tab
 * (or Resources / Prompts / OAuth Debugger / etc.) was silently a no-op:
 * the host change updated `initialize.clientCapabilities` for *new*
 * connects, but already-connected servers stayed up with stale caps and
 * any servers the new host didn't claim stayed up unnecessarily.
 *
 * Module-level dedupe inside the hook means the per-tab mounts can stay
 * in place without firing twice — first mount wins for a given
 * `(projectId, hostScope, reconciliationKey)` tuple.
 *
 * Renders nothing.
 */
export function ActiveHostServerReconciler({
  projectId,
  isAuthenticated,
  activeHost,
  activeHostId,
}: ActiveHostServerReconcilerProps) {
  const { servers: projectServersList } = useProjectServers({
    projectId,
    isAuthenticated,
  });

  // Every saved connector is required: sphinx starts up with all of them
  // connected (the host's `serverIds` subset was an mcpjam concept; targets
  // do not pick servers). While `projectServersList` is loading we resolve to
  // an empty list; the connect-required pass is keyed on a non-null
  // `candidateNamesKey`, so it stays quiet until the catalog arrives and the
  // candidate set materializes, at which point it fires exactly once. OAuth
  // connectors reuse their stored token; without one they land in the
  // disconnected "requires authorization" state and a manual reconnect
  // starts the flow — nothing here blocks the app.
  // ...except one the operator switched off on Connectors (`config.enabled`
  // is false). A disabled connector is never required and never reconnected,
  // which is what makes disabling a failing connector actually stop it.
  const requiredServerNames = useMemo(
    () =>
      (projectServersList ?? [])
        .filter((s) => s.enabled !== false)
        .map((s) => s.name),
    [projectServersList]
  );

  const disabledServerNames = useMemo(
    () =>
      new Set(
        (projectServersList ?? [])
          .filter((s) => s.enabled === false)
          .map((s) => s.name)
      ),
    [projectServersList]
  );

  // An OAuth callback in flight (`/oauth/callback?code=…`) must not be raced by
  // the page-load connect burst: its token-less attempt for the server being
  // authorized wiped the pending flow (see useAutoConnectProjectServers.paused).
  const { pathname } = useLocation();
  const oauthCallbackInFlight = pathname === "/oauth/callback";
  useAutoConnectProjectServers({
    projectId,
    paused: oauthCallbackInFlight,
    // Scope key is the explicit host id when one is picked; otherwise the
    // host config's own id (so swapping the project default to a different
    // host still counts as a scope change).
    // Connect on load even before any client is active (a fresh browser landing
    // on /connectors has no previewed client yet): the scope key only has to be
    // non-null for the connect pass to run; a real client id still rotates it.
    hostScopeKey: activeHostId ?? activeHost?.id ?? "default",
    requiredServerNames,
  });

  // Single source of truth: the Playground active server set
  // (`selectedMultipleServers`) mirrors the runtime set that is connected or
  // reconnecting. Keeping "connecting" active prevents the Playground tools
  // pane from blinking empty during a client-switch reconnect. The Servers tab
  // owns connectivity; everything else reflects it. Guarded by set-equality so
  // we never dispatch (and never loop) when the mirror already matches.
  const sharedAppState = useSharedAppState();
  const { setSelectedServerNames } = useServerActions();
  // A disabled connector is dropped from the mirror even if its runtime
  // connection is still winding down, so chat and tools requests stop
  // including it the moment it is switched off.
  // ...and minus the chat's explicit ad-hoc deselections: the mirror used to
  // force selection = connected-set, so turning a connector off for one chat
  // was reverted on the next render (2026-08-25). The connector stays
  // connected — only its membership in this chat's selection is subtracted.
  const deselectedConnectors = useDeselectedConnectors();
  const activeRuntimeNames = useMemo(
    () =>
      Object.entries(sharedAppState.servers)
        .filter(
          ([name, server]) =>
            isActiveRuntimeStatus(server.connectionStatus) &&
            !disabledServerNames.has(name) &&
            !deselectedConnectors.has(name)
        )
        .map(([name]) => name),
    [sharedAppState.servers, disabledServerNames, deselectedConnectors]
  );
  useEffect(() => {
    if (
      !sameNameSet(activeRuntimeNames, sharedAppState.selectedMultipleServers)
    ) {
      setSelectedServerNames(activeRuntimeNames);
    }
  }, [
    activeRuntimeNames,
    sharedAppState.selectedMultipleServers,
    setSelectedServerNames,
  ]);

  // Server-restart heal: connector MCP sessions live in the SERVER's memory, so
  // a deploy swaps them out from under an open tab — the cards still read
  // "connected" while tools/list returns nothing (0 tools until a hard refresh,
  // 2026-08-25). /health-db carries a per-process bootId; when it changes after
  // the first observation, reconnect every active connector through the same
  // non-interactive path the client-switch recycle uses (no OAuth popups, no
  // per-server toasts) and drop the sphinx query caches. Poll errors are the
  // swap window itself — ignored, the next tick sees the new process.
  const { reconnectServer } = useServerActions();
  const bootIdRef = useRef<string | null>(null);
  const activeRuntimeNamesRef = useRef(activeRuntimeNames);
  activeRuntimeNamesRef.current = activeRuntimeNames;
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      if (stopped || document.hidden) return;
      let bootId: string | undefined;
      try {
        bootId = (await sphinxApi.healthDb()).bootId;
      } catch {
        return;
      }
      if (!bootId || stopped) return;
      const prev = bootIdRef.current;
      bootIdRef.current = bootId;
      if (prev === null || prev === bootId) return;
      invalidateSphinxQueries();
      for (const name of activeRuntimeNamesRef.current) {
        void reconnectServer(name).catch(() => {});
      }
    };
    const timer = setInterval(() => void tick(), 30_000);
    void tick();
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [reconnectServer]);

  return null;
}
